import { AdElementRenderProps } from '@adflow/ad';
import { AdElementText, FontFace as AdflowFontFace, isTextElement, notEmpty } from '@adflow/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { EMPTY_FONT_FACES } from './fontConstants';

const PREVIEW_FONT_STYLE_ID = 'adflow-preview-font-faces';
const publishedFontFaceKeys = new Set<string>();

function cssString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** html-to-image only embeds @font-face rules. FontFace API loads are invisible to it. */
export function previewFontFaceCss(font: AdflowFontFace, uniqueFontFamily: string): string {
  return [
    '@font-face{',
    `font-family:"${cssString(uniqueFontFamily)}";`,
    `src:url("${cssString(font.src)}");`,
    `font-weight:${font.weight};`,
    `font-style:${font.style || 'normal'};`,
    `font-stretch:${font.stretch || 'normal'};`,
    '}',
  ].join('');
}

export function publishPreviewFontFace(font: AdflowFontFace, uniqueFontFamily: string): void {
  const key = `${uniqueFontFamily}|${font.weight}|${font.style || 'normal'}|${
    font.stretch || 'normal'
  }|${font.src}`;
  if (publishedFontFaceKeys.has(key) || typeof document === 'undefined') {
    return;
  }
  publishedFontFaceKeys.add(key);

  let style = document.getElementById(PREVIEW_FONT_STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = PREVIEW_FONT_STYLE_ID;
    document.head.appendChild(style);
  }
  style.appendChild(document.createTextNode(previewFontFaceCss(font, uniqueFontFamily)));
}

export function useFonts(customFonts: readonly AdflowFontFace[]) {
  const installedFonts = useRef<Set<string>>(new Set());
  const [ready, setReady] = useState(() => customFonts.length === 0);
  const [fontLoadResults, setFontLoadResults] = useState<
    Array<{
      font: AdflowFontFace;
      result: PromiseSettledResult<void>;
    }>
  >([]);

  const installFont = useCallback((fontFamily: string) => {
    installedFonts.current.add(fontFamily);
  }, []);

  const loadFont = useCallback(
    async (font: AdflowFontFace) => {
      // Create a unique font family name to avoid conflicts with page fonts
      // Prefix with 'adflow-preview-' to ensure isolation
      const uniqueFontFamily = `adflow-preview-${font.fontFamily}`;

      if (installedFonts.current.has(uniqueFontFamily)) {
        return;
      }
      const ff = new FontFace(uniqueFontFamily, `url("${font.src}")`, {
        style: font.style,
        weight: font.weight.toString(),
        stretch: font.stretch || 'normal',
      });

      await ff.load();

      publishPreviewFontFace(font, uniqueFontFamily);
      installFont(uniqueFontFamily);

      // Technically this is an exprimental API so the types
      // don't really work
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document.fonts as any).add(ff);

      return;
    },
    [installFont, installedFonts],
  );

  useEffect(() => {
    if (customFonts.length === 0) {
      return;
    }

    let mounted = true;

    (async () => {
      setReady(false);
      const result = await Promise.allSettled(customFonts.map(loadFont));

      if (mounted) {
        setReady(true);
        setFontLoadResults(customFonts.map((f, i) => ({ font: f, result: result[i] })));
      }
    })();

    return () => {
      mounted = false;
    };
  }, [customFonts, loadFont]);

  return [ready, fontLoadResults] as const;
}

const getCustomFontsToLoad = (
  customFonts: readonly AdflowFontFace[],
  textElements: AdElementText[],
): AdflowFontFace[] => {
  const fontFamiliesInUse = textElements
    .map((el) => el.data.style.fontFamily)
    .filter(notEmpty)
    .map((family) => {
      // Remove the 'adflow-preview-' prefix if present to match against customFonts
      return family.startsWith('adflow-preview-') ? family.slice('adflow-preview-'.length) : family;
    });

  return customFonts.filter((ff) => fontFamiliesInUse.some((family) => ff.fontFamily === family));
};

export function useLoadFontsForAd(
  adElements: AdElementRenderProps[],
  customFonts: readonly AdflowFontFace[],
) {
  const fontsToLoad = useMemo(() => {
    const textElements = adElements.filter(isTextElement);
    const filtered = getCustomFontsToLoad(customFonts, textElements);
    return filtered.length === 0 ? EMPTY_FONT_FACES : filtered;
  }, [adElements, customFonts]);

  return useFonts(fontsToLoad);
}
